/**
 * Authentication state for the console.
 *
 * Checks the signed-in session against GET /api/v1/auth/me on mount.
 * - authenticated: session cookie is valid; exposes the user's identity
 * - signed out: every route is redirected to /login by RequireAuth
 *
 * Sign-in is Entra ID SSO: the login button navigates to
 * /api/v1/auth/login, which redirects to Microsoft and back to the app
 * with a signed httpOnly session cookie.
 *
 * The session check is bounded by a timeout: a hung/stalled API (e.g. a
 * dead proxy keeping the connection open) must never leave the app stuck
 * on the "Checking your session…" screen — that looks exactly like an
 * unauthenticated render of a protected route. On timeout or network
 * error the user is treated as signed out and redirected to /login.
 */
import { useState, useEffect, useCallback } from 'react';

export interface AuthUser {
  userId: string;
  email: string;
  name: string | null;
  activeTenantId: string | null;
  activeTenant?: { id: string; slug: string; name: string } | null;
  memberships: Array<{ tenantId: string; role: string; tenant?: { id: string; slug: string; name: string } | null }>;
  /** Session cookie lifetime in seconds (from the API env). */
  sessionTtlSeconds?: number;
  permissions?: string[];
  mfa?: { authenticated: boolean; authTime: number | null };
}

export type AuthState =
  | { status: 'loading' }
  | { status: 'authenticated'; user: AuthUser }
  | { status: 'signedout' };

/** Upper bound for the session check. Also acts as a "stale" cutoff. */
export const AUTH_CHECK_TIMEOUT_MS = 10_000;

function fetchWithTimeout(url: string, init: RequestInit = {}, timeoutMs = AUTH_CHECK_TIMEOUT_MS): Promise<Response> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  return fetch(url, { ...init, credentials: 'same-origin', signal: ctrl.signal }).finally(() => clearTimeout(timer));
}

export function useAuth(): AuthState & { signOut: () => Promise<void>; switchTenant: (tenantId: string) => Promise<void> } & { activeTenant?: { id: string; slug: string; name: string } | null } {
  const [state, setState] = useState<AuthState>({ status: 'loading' });

  const load = useCallback(async () => {
    try {
      let res = await fetchWithTimeout('/api/v1/auth/me');
      if (res.ok) {
        let user = (await res.json()) as AuthUser;
        setState({ status: 'authenticated', user });
      } else if (res.status === 401 || res.status === 403) {
        setState({ status: 'signedout' });
      } else {
        // Server error: treat as signed out so the user can retry via login.
        setState({ status: 'signedout' });
      }
    } catch {
      // Network error, stall, or timeout: do not strand the user on the
      // session-check screen — route them to login so they can retry.
      setState({ status: 'signedout' });
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const switchTenant = useCallback(async (tenantId: string) => {
    const res = await fetchWithTimeout('/api/v1/auth/tenant', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ tenantId }) });
    if (!res.ok) throw new Error(res.status === 403 ? 'You no longer have access to that workspace' : 'Could not switch workspace');
    window.location.reload();
  }, []);

  const signOut = useCallback(async () => {
    try {
      await fetchWithTimeout('/api/v1/auth/logout', { method: 'POST' });
    } catch {
      // Best effort; clear client state regardless.
    }
    setState({ status: 'signedout' });
  }, []);

  return {
    ...state,
    signOut,
    switchTenant,
    activeTenant: state.status === 'authenticated' ? state.user.activeTenant : undefined,
  };
}

export function beginSignIn(): void {
  window.location.href = '/api/v1/auth/login';
}
